// AI opening scripts.
//
// The AI ran one hardcoded five-step opening — auto-worker, one unit, one production building,
// capture the home flank, develop at t>120 — identical in every match and for every faction.
// Even against the same difficulty twice in a row, the first two minutes were byte-identical.
//
// These are data-driven alternatives expressed entirely in the abstract DeckRoles vocabulary
// that resolveDeckRoles() already produces from the AI's own deck. No card ids, no faction ids:
// a faction lacking a given role simply skips that step, which the existing timeout-skip
// machinery already handles.

/** Which role a step plays, or a non-card action. */
export type OpeningAction =
  | { kind: 'setup' }
  | { kind: 'unit' }
  | { kind: 'production' }
  | { kind: 'forwardProduction' }
  | { kind: 'support' }
  | { kind: 'trap' }
  | { kind: 'captureFlank' }
  | { kind: 'develop' };

export interface OpeningStep {
  action: OpeningAction;
  /** earliest sim time this step may run */
  at: number;
  /** sim time after which the step is abandoned so the opening never stalls */
  skipAt: number;
}

export interface Opening {
  name: string;
  steps: OpeningStep[];
}

/**
 * Four openings covering the strategic spread. All timings // TEST.
 *
 * The first is the original script, kept verbatim so Easy (openingVariety 1) plays exactly what
 * the AI has always played and the change is opt-in by difficulty.
 */
export const OPENINGS: Opening[] = [
  {
    name: 'standard',
    steps: [
      { action: { kind: 'setup' }, at: 0, skipAt: 0 },
      { action: { kind: 'unit' }, at: 4, skipAt: 30 },
      { action: { kind: 'production' }, at: 15, skipAt: 60 },
      { action: { kind: 'captureFlank' }, at: 15, skipAt: 90 },
      { action: { kind: 'develop' }, at: 120, skipAt: 120 },
    ],
  },
  {
    name: 'economic',
    // Bank the early Power into a second production base before committing any army.
    steps: [
      { action: { kind: 'setup' }, at: 0, skipAt: 0 },
      { action: { kind: 'production' }, at: 12, skipAt: 55 },
      { action: { kind: 'unit' }, at: 30, skipAt: 70 },
      { action: { kind: 'captureFlank' }, at: 35, skipAt: 100 },
      { action: { kind: 'support' }, at: 70, skipAt: 110 },
      { action: { kind: 'develop' }, at: 130, skipAt: 130 },
    ],
  },
  {
    name: 'aggressive',
    // Units first and a forward production building, trading economy for early map pressure.
    steps: [
      { action: { kind: 'setup' }, at: 0, skipAt: 0 },
      { action: { kind: 'unit' }, at: 3, skipAt: 24 },
      { action: { kind: 'captureFlank' }, at: 8, skipAt: 60 },
      { action: { kind: 'unit' }, at: 20, skipAt: 55 },
      { action: { kind: 'forwardProduction' }, at: 40, skipAt: 90 },
      { action: { kind: 'develop' }, at: 95, skipAt: 95 },
    ],
  },
  {
    name: 'defensive',
    // Production and a home trap first; slower to the map, much harder to rush.
    steps: [
      { action: { kind: 'setup' }, at: 0, skipAt: 0 },
      { action: { kind: 'production' }, at: 14, skipAt: 60 },
      { action: { kind: 'trap' }, at: 30, skipAt: 75 },
      { action: { kind: 'unit' }, at: 35, skipAt: 80 },
      { action: { kind: 'captureFlank' }, at: 50, skipAt: 110 },
      { action: { kind: 'develop' }, at: 125, skipAt: 125 },
    ],
  },
];

/**
 * Pick an opening deterministically from the match seed and player slot.
 *
 * Deliberately NOT from state.rng: the director runs after sim.tick(), so drawing from the
 * shared stream would couple AI decisions to sim RNG consumption and make any future sim
 * randomness change every AI's opening. A pure hash of (seed, player) is replay-stable and
 * independent.
 */
export function pickOpening(seed: number, player: number, variety: number): Opening {
  const count = Math.max(1, Math.min(variety, OPENINGS.length));
  let h = (seed ^ (player * 0x9e3779b9)) >>> 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return OPENINGS[h % count];
}
